import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../apiClient';
import { FaEdit, FaEye, FaEyeSlash, FaTrash, FaClone, FaChartBar, FaSync, FaPlus, FaUsers, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import CreateEditPlanModal from '../../components/subscription/CreateEditPlanModal';
import PlanAnalyticsModal from '../../components/subscription/PlanAnalyticsModal';
import BulkPriceUpdateModal from '../../components/subscription/BulkPriceUpdateModal';
import UserSubscriptionManagementModal from '../../components/subscription/UserSubscriptionManagementModal';
import './AdminSubscriptionPlans.css';

function AdminSubscriptionPlans() {
  const { user: currentUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [showUserManagementModal, setShowUserManagementModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [subscriptions, setSubscriptions] = useState([]);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    if (currentUser?.role === 'Admin') {
      fetchPlans();
    } else {
      setError('Admin access required');
      setLoading(false);
    }
  }, [currentUser]);

  const fetchPlans = async () => {
    try {
      const response = await apiClient.get('/admin/subscription-plans');
      setPlans(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch subscription plans');
      setLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const response = await apiClient.get('/auth/admin/subscriptions');
      setSubscriptions(response.data);
    } catch (err) {
      console.error('Failed to fetch subscriptions:', err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await apiClient.get('/employees');
      setEmployees(response.data.employees || []);
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  };

  const handleToggleVisibility = async (planId, currentVisibility) => {
    try {
      const endpoint = currentVisibility ? 'hide' : 'show';
      await apiClient.post(`/admin/subscription-plans/${planId}/${endpoint}`);
      setSuccessMessage(`Plan ${currentVisibility ? 'hidden' : 'shown'} successfully`);
      fetchPlans();
    } catch (err) {
      setError(`Failed to ${currentVisibility ? 'hide' : 'show'} plan`);
    }
  };

  const handleToggleActive = async (planId, currentActive) => {
    try {
      const endpoint = currentActive ? 'deactivate' : 'activate';
      await apiClient.post(`/admin/subscription-plans/${planId}/${endpoint}`);
      setSuccessMessage(`Plan ${currentActive ? 'deactivated' : 'activated'} successfully`);
      fetchPlans();
    } catch (err) {
      setError(`Failed to ${currentActive ? 'deactivate' : 'activate'} plan`);
    }
  };

  const handleDuplicatePlan = async (planId) => {
    const planName = prompt('Enter name for the duplicated plan:');
    if (!planName) return;

    try {
      await apiClient.post(`/admin/subscription-plans/${planId}/duplicate`, { new_name: planName });
      setSuccessMessage('Plan duplicated successfully');
      fetchPlans();
    } catch (err) {
      setError('Failed to duplicate plan');
    }
  };

  const handleSyncStripe = async (planId) => {
    try {
      await apiClient.post(`/admin/subscription-plans/${planId}/sync-stripe`);
      setSuccessMessage('Plan synced with Stripe successfully');
      fetchPlans();
    } catch (err) {
      setError('Failed to sync with Stripe');
    }
  };

  const handleChangeUserPlan = async (userId, newPlanId) => {
    try {
      await apiClient.put(`/auth/admin/users/${userId}/subscription`, { plan_definition_id: newPlanId });
      setSuccessMessage('User plan updated successfully');
      fetchSubscriptions();
    } catch (err) {
      setError('Failed to change user plan');
    }
  };

  const handleAssignPlan = async (employeeId, planId) => {
    try {
      await apiClient.post('/auth/admin/assign-plan', {
        employee_id: employeeId,
        plan_definition_id: planId
      });
      setSuccessMessage('Plan assigned successfully');
      fetchSubscriptions();
    } catch (err) {
      setError('Failed to assign plan');
    }
  };

  const handleCleanupOrphaned = async () => {
    if (!window.confirm('Are you sure you want to clean up orphaned subscriptions? This will permanently delete subscriptions for users who no longer exist.')) {
      return;
    }
    
    try {
      const response = await apiClient.delete('/auth/admin/cleanup-orphaned-subscriptions');
      setSuccessMessage(`${response.data.message}. Found and cleaned up: ${response.data.orphaned_subscriptions.length} orphaned subscriptions.`);
      fetchSubscriptions();
    } catch (err) {
      setError('Failed to cleanup orphaned subscriptions');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan? This action cannot be undone.')) {
      return;
    }
    
    try {
      await apiClient.delete(`/admin/subscription-plans/${planId}`);
      setSuccessMessage('Plan deleted successfully');
      fetchPlans();
    } catch (err) {
      setError('Failed to delete plan: ' + (err.response?.data?.error || err.message));
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const formatFeatures = (features) => {
    if (!features) return 'None';
    return Object.entries(features)
      .filter(([_, value]) => value)
      .map(([key]) => key.replace(/_/g, ' '))
      .join(', ');
  };

  const formatLimits = (limits) => {
    if (!limits) return 'None';
    return Object.entries(limits)
      .map(([key, value]) => `${key}: ${value === -1 ? 'Unlimited' : value}`)
      .join(', ');
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (error && currentUser?.role !== 'Admin') return <div className="error">{error}</div>;

  return (
    <div className="admin-subscription-plans">
      <div className="page-header">
        <h1>Subscription Plans Management</h1>
        <div className="header-actions">
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            <FaPlus /> Create New Plan
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => {
              fetchSubscriptions();
              fetchEmployees();
              setShowUserManagementModal(true);
            }}
          >
            <FaUsers /> Manage Users
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => setShowBulkUpdateModal(true)}
          >
            Bulk Price Update
          </button>
        </div>
      </div>

      {successMessage && (
        <div className="success-message">
          {successMessage}
          <button onClick={() => setSuccessMessage('')}>×</button>
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      <div className="plans-grid">
        {plans.map(plan => (
          <div key={plan.id} className={`plan-card ${!plan.is_active ? 'inactive' : ''}`}>
            <div className="plan-header">
              <h3>{plan.name}</h3>
              <div className="plan-status">
                {plan.is_active ? (
                  <span className="badge badge-success">Active</span>
                ) : (
                  <span className="badge badge-danger">Inactive</span>
                )}
                {!plan.is_visible && (
                  <span className="badge badge-warning">Hidden</span>
                )}
              </div>
            </div>
            
            <div className="plan-pricing">
              <div className="price">
                <span className="amount">{formatPrice(plan.price_monthly)}</span>
                <span className="period">/month</span>
              </div>
              <div className="annual-price">
                {formatPrice(plan.price_annual)}/year
              </div>
            </div>

            <div className="plan-details">
              <p className="description">{plan.description}</p>
              
              {plan.trial_days > 0 && (
                <p className="trial-info">
                  {plan.trial_days} day free trial
                </p>
              )}

              <div className="features">
                <h4>Features</h4>
                <p>{formatFeatures(plan.features)}</p>
              </div>

              <div className="limits">
                <h4>Limits</h4>
                <p>{formatLimits(plan.limits)}</p>
              </div>

              <div className="subscription-info">
                <p>Active Subscriptions: <strong>{plan.active_subscriptions || 0}</strong></p>
              </div>
            </div>

            <div className="plan-actions">
              <button
                className="btn-icon"
                onClick={() => {
                  setSelectedPlan(plan);
                  setShowEditModal(true);
                }}
                title="Edit Plan"
              >
                <FaEdit />
              </button>
              
              <button
                className="btn-icon"
                onClick={() => handleToggleVisibility(plan.id, plan.is_visible)}
                title={plan.is_visible ? 'Hide Plan' : 'Show Plan'}
              >
                {plan.is_visible ? <FaEyeSlash /> : <FaEye />}
              </button>

              <button
                className="btn-icon"
                onClick={() => handleToggleActive(plan.id, plan.is_active)}
                title={plan.is_active ? 'Deactivate' : 'Activate'}
              >
                {plan.is_active ? <FaToggleOn /> : <FaToggleOff />}
              </button>

              <button
                className="btn-icon"
                onClick={() => handleDuplicatePlan(plan.id)}
                title="Duplicate Plan"
              >
                <FaClone />
              </button>

              <button
                className="btn-icon"
                onClick={() => {
                  setSelectedPlan(plan);
                  setShowAnalyticsModal(true);
                }}
                title="View Analytics"
              >
                <FaChartBar />
              </button>

              <button
                className="btn-icon"
                onClick={() => handleSyncStripe(plan.id)}
                title="Sync with Stripe"
              >
                <FaSync />
              </button>

              <button
                className="btn-icon btn-danger"
                onClick={() => handleDeletePlan(plan.id)}
                title="Delete Plan"
              >
                <FaTrash />
              </button>
            </div>
          </div>
        ))}
      </div>

      {showCreateModal && (
        <CreateEditPlanModal
          plan={null}
          onClose={() => setShowCreateModal(false)}
          onSave={() => {
            setShowCreateModal(false);
            fetchPlans();
            setSuccessMessage('Plan created successfully');
          }}
        />
      )}

      {showEditModal && selectedPlan && (
        <CreateEditPlanModal
          plan={selectedPlan}
          onClose={() => {
            setShowEditModal(false);
            setSelectedPlan(null);
          }}
          onSave={() => {
            setShowEditModal(false);
            setSelectedPlan(null);
            fetchPlans();
            setSuccessMessage('Plan updated successfully');
          }}
        />
      )}

      {showAnalyticsModal && selectedPlan && (
        <PlanAnalyticsModal
          plan={selectedPlan}
          onClose={() => {
            setShowAnalyticsModal(false);
            setSelectedPlan(null);
          }}
        />
      )}

      {showBulkUpdateModal && (
        <BulkPriceUpdateModal
          plans={plans}
          onClose={() => setShowBulkUpdateModal(false)}
          onUpdate={() => {
            setShowBulkUpdateModal(false);
            fetchPlans();
            setSuccessMessage('Prices updated successfully');
          }}
        />
      )}

      {showUserManagementModal && (
        <UserSubscriptionManagementModal
          plans={plans}
          subscriptions={subscriptions}
          employees={employees}
          onClose={() => setShowUserManagementModal(false)}
          onChangeUserPlan={handleChangeUserPlan}
          onAssignPlan={handleAssignPlan}
          onCleanupOrphaned={handleCleanupOrphaned}
        />
      )}
    </div>
  );
}

export default AdminSubscriptionPlans;